import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  fetchSitrepById,
  keys,
  type PollSitrepResponse,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import { ReportsEmptyState } from 'components/Reports/ReportsEmptyState';
import { useAorText } from 'utils/aorTerminology';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { sitrepInPublishedRange } from 'components/Reports/utils/partitionPublishedSitreps';

const sitrepMatchesSearch = (
  sitrepData: PollSitrepResponse | undefined,
  query: string
): boolean => {
  if (!sitrepData?.sections) return false;
  return sitrepData.sections.some(
    (section) =>
      section.name?.toLowerCase().includes(query) ||
      section.section_versions?.some((version) =>
        version.sitrep_section_text?.toLowerCase().includes(query)
      )
  );
};

type HistoricalReportViewProps = {
  historicalSitreps: GetSitrepsResponse[];
  isLoading: boolean;
  isError: boolean;
  scopeName: string;
};

const HistoricalReportView: React.FC<HistoricalReportViewProps> = ({
  historicalSitreps,
  isLoading,
  isError,
  scopeName,
}) => {
  const aorText = useAorText();
  const [searchQuery, setSearchQuery] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const sitrepQueries = useQueries({
    queries: historicalSitreps.map((sitrep) => ({
      queryKey: keys.byId(sitrep.id),
      queryFn: () => fetchSitrepById(sitrep.id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const rangeActive = Boolean(rangeStart || rangeEnd);

  const filteredSitreps = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    return historicalSitreps.filter((sitrep, index) => {
      if (!sitrepInPublishedRange(sitrep, rangeStart, rangeEnd)) return false;
      if (!trimmed) return true;
      if (sitrep.name?.toLowerCase().includes(trimmed)) return true;
      if (sitrep.description?.toLowerCase().includes(trimmed)) return true;
      const sitrepData = sitrepQueries[index]?.data;
      return sitrepMatchesSearch(sitrepData, trimmed);
    });
  }, [historicalSitreps, rangeEnd, rangeStart, searchQuery, sitrepQueries]);

  return (
    <div className="reports-tabs tw:pt-3">
      {isError ? (
        <ReportsEmptyState
          icon="error"
          title="Error loading reports"
          description={aorText(
            'Try refreshing or selecting a different AOR or incident.'
          )}
        />
      ) : isLoading ? (
        <Spinner className="tw:size-6" />
      ) : historicalSitreps.length > 0 ? (
        <>
          <div className="tw:mb-3 tw:flex tw:flex-col tw:gap-2">
            <InputGroup>
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                type="text"
                placeholder="Search historical..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search historical reports"
              />
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  size="icon-xs"
                  aria-label="Clear historical report search"
                  title="Clear search"
                  disabled={!searchQuery.trim()}
                  onClick={() => setSearchQuery('')}
                >
                  <X />
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
            <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-2">
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="historical-range-start">Start</Label>
                <Input
                  id="historical-range-start"
                  type="datetime-local"
                  value={rangeStart}
                  onChange={(event) => setRangeStart(event.target.value)}
                  className="tw:w-auto"
                />
              </div>
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="historical-range-end">End</Label>
                <Input
                  id="historical-range-end"
                  type="datetime-local"
                  value={rangeEnd}
                  onChange={(event) => setRangeEnd(event.target.value)}
                  className="tw:w-auto"
                />
              </div>
            </div>
          </div>

          {filteredSitreps.length > 0 ? (
            filteredSitreps.map((sitrep) => (
              <div key={sitrep.id} className="reports-tabs__latest-sitrep">
                <CurrentReportsSitrepCard sitrep={sitrep} hideDraftButton />
              </div>
            ))
          ) : (
            <ReportsEmptyState
              title={
                rangeActive
                  ? `No reports for ${scopeName} in that time range`
                  : `No reports for ${scopeName} match your search`
              }
            />
          )}
        </>
      ) : (
        <ReportsEmptyState title={`No reports for ${scopeName} yet`} />
      )}
    </div>
  );
};

export default HistoricalReportView;
